/**
 * After you remove a focused element from the DOM, document.activeElement will be at the body.
 * So if you use a focus trap NVDA can get out of the trap and traverse accessibility DOM out of the scope of the trap.
 * This hook can prevent it.
 */

import { useRef, useEffect, useCallback } from 'react';

function isElementFocusable(element: Element | HTMLElement | HTMLButtonElement) {
    // https://html.spec.whatwg.org/multipage/interaction.html#dom-tabindex
    if ('tabIndex' in element && element.tabIndex === -1) {
        return false;
    }

    if ('disabled' in element && element.disabled) {
        return false;
    }

    if (element.tagName === 'A' && !element.hasAttribute('href')) {
        return false;
    }

    if ('hidden' in element && element.hidden) {
        return false;
    }

    return true;
}

function getFocusableElements(root: HTMLElement) {
    return Array.from(root.querySelectorAll<HTMLElement>('*')).filter(isElementFocusable);
}

function tryToFocus(element: Element | HTMLElement): boolean {
    if (!('focus' in element)) {
        return false;
    }

    try {
        element.focus();
    } catch {
        // not applicable
    }

    return document.activeElement === element;
}

function moveFocus(
    elements: HTMLElement[],
    startElement: HTMLElement | null,
    order: 'forward' | 'backward'
) {
    if (!elements.length) {
        return;
    }
    const elementsToTraverse = [...elements];

    if (order === 'backward') {
        elementsToTraverse.reverse();
    }

    let startIndex = startElement ? elementsToTraverse.indexOf(startElement) : -1;
    if (startIndex === -1) {
        // To handle lack of startElement I add fake element at the end of the list.
        // This can simplify the algorhitm.
        startIndex = elements.length;
        const fakeElement = document.createElement('div');
        elementsToTraverse.push(fakeElement);
    }

    let currentIndex = (startIndex + 1) % elementsToTraverse.length;

    while (currentIndex !== startIndex) {
        const elementCandidate = elementsToTraverse[currentIndex];

        if (elementCandidate && tryToFocus(elementCandidate)) {
            return;
        }

        currentIndex = (currentIndex + 1) % elementsToTraverse.length;
    }
}

function sortElementsByFocusOrder(elements: HTMLElement[]) {
    return elements
        .map((element) => ({ element, tabIndex: element.tabIndex }))
        .sort(({ tabIndex: tabIndexLeft }, { tabIndex: tabIndexRight }) => {
            if (tabIndexLeft === tabIndexRight) {
                return 0;
            }

            if (tabIndexLeft === 0) {
                return 1;
            }

            if (tabIndexRight === 0) {
                return -1;
            }

            return tabIndexLeft - tabIndexRight;
        })
        .map(({ element }) => element);
}

function checkEventTargetIsHtmlElement(eventTarget: EventTarget): eventTarget is HTMLElement {
    return 'tabIndex' in eventTarget;
}

export function useAutoFocusAfterRemoveFocusedElement(isEnabled: boolean) {
    const rootElementRef = useRef<HTMLElement>();
    const isEnabledRef = useRef(isEnabled);
    const lastElementRef = useRef<HTMLElement>();

    const handleMutation: MutationCallback = useCallback((mutationList) => {
        if (!rootElementRef.current) {
            return;
        }

        const isChildListChanged = [...mutationList].some(({ type }) => type === 'childList');

        if (!isChildListChanged) {
            return;
        }

        if (!lastElementRef.current) {
            return;
        }

        if (rootElementRef.current.contains(lastElementRef.current)) {
            return;
        }

        const focusableElements = getFocusableElements(rootElementRef.current);
        const sortedFocusableElements = sortElementsByFocusOrder(focusableElements);
        moveFocus(sortedFocusableElements, null, 'forward');
    }, []);

    const observerRef = useRef<null | MutationObserver>(null);

    useEffect(() => {
        observerRef.current = new MutationObserver(handleMutation);
    }, [handleMutation]);

    const observeMutation = () => {
        if (rootElementRef.current) {
            observerRef.current?.observe(rootElementRef.current, {
                childList: true,
                subtree: true,
            });
        }
    };

    const handleFocus = useCallback(({ target }: FocusEvent) => {
        if (!isEnabledRef.current) {
            return;
        }

        if (!rootElementRef.current) {
            return;
        }

        if (!target) {
            return;
        }

        if (!checkEventTargetIsHtmlElement(target)) {
            return;
        }

        if (!rootElementRef.current.contains(target)) {
            return;
        }

        lastElementRef.current = target;
    }, []);

    useEffect(() => {
        const { current: observer } = observerRef;

        isEnabledRef.current = isEnabled;

        if (isEnabled) {
            observeMutation();
        } else {
            observer?.disconnect();
        }

        return () => observer?.disconnect();
    }, [isEnabled]);

    useEffect(() => {
        document.addEventListener('focus', handleFocus, true);
        return () => document.removeEventListener('focus', handleFocus, true);
    }, [handleFocus]);

    const setRootElementRef = useCallback((element?: HTMLElement | null) => {
        if (!element) {
            return;
        }

        rootElementRef.current = element;

        if (isEnabledRef.current) {
            observerRef.current?.disconnect();
            observeMutation();
        }
    }, []);

    return setRootElementRef;
}
